<script setup lang="ts">
import {
  ref,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  watch,
} from "vue";
import type { UploadIntent } from "@desfecha/contracts";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import Button from "./Button.vue";
import Dropdown from "./Dropdown.vue";
import EmojiPicker from "./EmojiPicker.vue";
import Modal from "./Modal.vue";
import Switch from "./Switch.vue";
import Select from "./Select.vue";
import MediaComposer from "./MediaComposer.vue";
import CameraCapture from "./CameraCapture.vue";
import FormatBar from "./FormatBar.vue";
import { addMessage, state } from "../composables/chat";
import { toast } from "../composables/feedback";
import { useEscapeLayer } from "../composables/escape";
import { barHeight, bucket } from "../composables/waveform";
import { acceptFor, type PreparedFile } from "../composables/mediaPrep";
import { locale, t } from "../i18n";
import { quoteText } from "../api/mappers";
import type { LinkPreview, Message } from "../data/demo";
import { waPieces } from "../composables/waFormat";
import {
  activeFormats,
  continueList,
  toggleInline,
  toggleLines,
  type Format,
  type TextEdit,
} from "../composables/composerFormat";
import { caretPoint } from "../composables/textareaCaret";
import { mayTakeFocus } from "../composables/composerFocus";
import { draftOf, setDraft } from "../composables/composerDrafts";
const props = withDefaults(
  defineProps<{
    chatId: string;
    /** Takes the cursor when it appears (the conversation's own box, not a demo). */
    autofocus?: boolean;
    /** Keeps unsent text as the chat's draft, as WhatsApp Web (the conversation's own box). */
    drafts?: boolean;
    reply?: Message;
    edit?: Message;
    /** When provided, sending goes through this function instead of the local demo store. */
    send?: (input: {
      text: string;
      reply?: Message;
      /** The text's first link goes with the preview shown above the text. */
      linkPreview?: boolean;
    }) => Promise<void> | void;
    /** When provided, a link typed in the text shows its preview above it, as on WhatsApp. */
    previewLink?: (url: string) => Promise<LinkPreview | null>;
    attachments?: boolean;
    audio?: boolean;
    /** Canned replies offered when the text starts with "/". */
    snippets?: Array<{
      id: string;
      title: string;
      shortcut: string | null;
      body: string;
    }>;
    renderSnippet?: (body: string) => string;
    /** When provided, prepared files go to the API (upload + send, in order). */
    sendFiles?: (items: PreparedFile[]) => Promise<void> | void;
    /** When provided, a recorded voice note goes to the API. */
    sendVoice?: (input: { blob: Blob; durationMs: number }) => Promise<void>;
    /** Shows "Contato" in the attach menu and emits `contacts` (API mode). */
    contacts?: boolean;
    /** CSS selector where the media composer covers the conversation (like WhatsApp Web). */
    overlayTarget?: string;
    /** Name shown on the reply card when quoting the contact's message. */
    contactName?: string;
  }>(),
  { attachments: true, audio: true },
);
const emit = defineEmits<{ clear: []; sent: []; contacts: [] }>();
const api = computed(() => Boolean(props.sendFiles));
/** Reply card like WhatsApp's: who wrote the quoted message, its text or media label. */
const quoteAuthor = computed(() => {
  if (props.edit) return t("composer.edit");
  if (props.reply?.out) return t("media.viewer.you");
  return (
    props.contactName ??
    state.chats.find((c) => c.id === props.chatId)?.name ??
    t("composer.reply")
  );
});
const quoteLine = computed(() => {
  const m = props.edit || props.reply;
  return m ? quoteText(m) : "";
});
const quoteThumb = computed(() => {
  const m = props.reply;
  return m && (m.kind === "image" || m.kind === "video") && m.src ? m.src : "";
});
/**
 * The preview of the text's first link, as WhatsApp Web shows it above the text: the site while it
 * loads, then the picture, title and description; closed with its ×, the message goes without it.
 */
const linkCard = ref<{
  url: string;
  state: "loading" | "ready";
  preview: LinkPreview | null;
} | null>(null);
const closedLinks = new Set<string>();
let linkTimer: ReturnType<typeof setTimeout> | undefined;
const siteOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return url;
  }
};
function watchLink(value: string) {
  clearTimeout(linkTimer);
  if (!props.previewLink) return;
  const url = waPieces(value).find((p) => p.href)?.href ?? null;
  if (!url || closedLinks.has(url)) {
    linkCard.value = null;
    return;
  }
  if (linkCard.value?.url === url) return;
  linkTimer = setTimeout(async () => {
    linkCard.value = { url, state: "loading", preview: null };
    const preview = await props.previewLink!(url).catch(() => null);
    // The text may have changed while the page was read.
    if (linkCard.value?.url !== url) return;
    linkCard.value = preview ? { url, state: "ready", preview } : null;
  }, 500);
}
function closeLink() {
  if (linkCard.value) closedLinks.add(linkCard.value.url);
  linkCard.value = null;
  input.value?.focus();
}
const text = ref(props.drafts ? draftOf(props.chatId) : ""),
  input = ref<HTMLTextAreaElement>(),
  fileInput = ref<HTMLInputElement>(),
  expressions = ref(false),
  menu = ref({ open: false, x: 0, y: 0 }),
  modal = ref(""),
  question = ref(""),
  options = ref(["", ""]),
  multiple = ref(true),
  anonymous = ref(false),
  endPoll = ref(false),
  eventName = ref(""),
  eventDate = ref("2026-09-21T14:00"),
  contact = ref("Clara Martins"),
  accept = ref(""),
  intent = ref<UploadIntent>("media"),
  media = ref<{ files: File[]; intent: UploadIntent } | null>(null),
  camera = ref(false),
  endDate = ref(""),
  recording = ref(false),
  paused = ref(false),
  seconds = ref(0),
  recorded = ref(""),
  recordingError = ref(""),
  sendingVoice = ref(false);
let recorder: MediaRecorder | undefined,
  stream: MediaStream | undefined,
  timer: ReturnType<typeof setInterval> | undefined,
  chunks: BlobPart[] = [],
  recordedBlob: Blob | null = null,
  audioCtx: AudioContext | undefined,
  levelTimer: ReturnType<typeof setInterval> | undefined,
  previewAudio: HTMLAudioElement | undefined;
/** The microphone's loudness, ten times a second: the live waveform, as on WhatsApp. */
const levels = ref<number[]>([]);
/** Paused, the recording so far can be heard before going on or sending. */
const preview = ref<{
  url: string;
  playing: boolean;
  position: number;
} | null>(null);
const LIVE_BARS = 36;
const liveBars = computed(() => {
  const tail = levels.value.slice(-LIVE_BARS);
  return [
    ...Array<undefined>(LIVE_BARS - tail.length).fill(undefined),
    ...tail.map((l) => barHeight(l)),
  ];
});
const pausedBars = computed(() =>
  bucket(levels.value, LIVE_BARS).map((l) => barHeight(l)),
);
const previewProgress = computed(() =>
  preview.value && seconds.value
    ? Math.min(1, preview.value.position / seconds.value)
    : 0,
);
const previewClock = computed(() => {
  const s =
    preview.value?.playing || preview.value?.position
      ? Math.round(preview.value.position)
      : seconds.value;
  return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
});
const snippetIndex = ref(0);
const snippetQuery = computed(() =>
  text.value.startsWith("/") && !text.value.includes("\n")
    ? text.value.slice(1).trim().toLowerCase()
    : null,
);
const snippetMatches = computed(() => {
  const q = snippetQuery.value;
  if (q === null || !props.snippets?.length) return [];
  return props.snippets
    .filter(
      (sn) =>
        !q ||
        (sn.shortcut ?? "").toLowerCase().includes(q) ||
        sn.title.toLowerCase().includes(q),
    )
    .slice(0, 8);
});
watch(snippetMatches, () => (snippetIndex.value = 0));
function pickSnippet(sn: { body: string }) {
  text.value = props.renderSnippet ? props.renderSnippet(sn.body) : sn.body;
  nextTick(() => input.value?.focus());
}
function snippetKey(e: KeyboardEvent): boolean {
  if (!snippetMatches.value.length) return false;
  if (e.key === "ArrowDown") {
    snippetIndex.value = (snippetIndex.value + 1) % snippetMatches.value.length;
  } else if (e.key === "ArrowUp") {
    snippetIndex.value =
      (snippetIndex.value - 1 + snippetMatches.value.length) %
      snippetMatches.value.length;
  } else if (e.key === "Enter" || e.key === "Tab") {
    pickSnippet(snippetMatches.value[snippetIndex.value]!);
  } else return false;
  e.preventDefault();
  return true;
}
const duration = computed(
  () =>
    Math.floor(seconds.value / 60) +
    ":" +
    String(seconds.value % 60).padStart(2, "0"),
);
useEscapeLayer(
  () => props.edit || props.reply,
  () => emit("clear"),
);
useEscapeLayer(expressions, () => {
  expressions.value = false;
  input.value?.focus();
});
useEscapeLayer(recording, cancelRecording);
// The quick replies list ("/"): Escape drops the shortcut being typed, which closes it.
useEscapeLayer(
  () => snippetMatches.value.length > 0,
  () => {
    text.value = "";
    input.value?.focus();
  },
);
watch(
  () => props.edit,
  async (m) => {
    // Editing a sent message borrows the box; the draft comes back when the edit ends.
    text.value = m?.text || (props.drafts ? draftOf(props.chatId) : "");
    await nextTick();
    input.value?.focus();
  },
);
/**
 * The cursor stays in the box, as on WhatsApp Web: when a conversation opens (also after F5), when
 * another one is chosen and after anything is sent.
 */
function keepFocus() {
  nextTick(() => {
    const el = input.value;
    if (!el) return;
    const at = document.activeElement;
    const ok = mayTakeFocus({
      visible: el.offsetParent !== null,
      finePointer: window.matchMedia("(pointer: fine)").matches,
      elsewhereInField:
        at instanceof HTMLElement &&
        at !== el &&
        at.matches("input, textarea, select, [contenteditable='true']"),
      dialogOpen: !!document.querySelector("[aria-modal='true']"),
    });
    if (ok) el.focus();
  });
}
onMounted(() => {
  if (props.autofocus) keepFocus();
});
watch(
  () => props.chatId,
  () => {
    text.value = props.drafts ? draftOf(props.chatId) : "";
    expressions.value = false;
    media.value = null;
    camera.value = false;
    cancelRecording();
    emit("clear");
    keepFocus();
  },
);
watch(
  () => props.reply,
  async () => {
    await nextTick();
    input.value?.focus();
  },
);
watch(text, watchLink);
watch(text, (value) => {
  if (props.drafts && !props.edit) setDraft(props.chatId, value);
});
/**
 * The box grows with the text up to 140 px and shrinks with it, whatever changed it: typing,
 * sending (back to one line), a canned reply, an emoji or a failed send putting the text back.
 */
function fitInput() {
  const el = input.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = Math.min(140, el.scrollHeight) + "px";
}
watch(text, () => nextTick(fitInput));
onBeforeUnmount(() => clearTimeout(linkTimer));
/**
 * WhatsApp Web's formatting: the bar over a selection, ⌘/Ctrl+B, I and Shift+X, and lists that
 * carry on with Shift+Enter. Each change goes through the browser's own editing, so ⌘Z undoes it.
 */
const formatBar = ref<{ x: number; y: number; active: Format[] } | null>(null);
function updateFormatBar() {
  const el = input.value;
  if (
    !el ||
    document.activeElement !== el ||
    el.selectionStart === el.selectionEnd
  ) {
    formatBar.value = null;
    return;
  }
  const box = el.getBoundingClientRect();
  const at = caretPoint(el, el.selectionStart);
  formatBar.value = {
    x: at.x,
    y: Math.max(at.y, box.top),
    active: activeFormats(text.value, el.selectionStart, el.selectionEnd),
  };
}
function applyEdit(edit: TextEdit) {
  const el = input.value;
  if (!el) return;
  el.focus();
  el.setSelectionRange(edit.from, edit.to);
  const done =
    edit.insert || edit.from === edit.to
      ? document.execCommand("insertText", false, edit.insert)
      : document.execCommand("delete");
  if (!done) {
    el.setRangeText(edit.insert, edit.from, edit.to, "end");
    el.dispatchEvent(new Event("input", { bubbles: true }));
  }
  el.setSelectionRange(edit.selStart, edit.selEnd);
}
function applyFormat(format: Format) {
  const el = input.value;
  if (!el) return;
  const { selectionStart: a, selectionEnd: b } = el;
  applyEdit(
    format === "numbered" || format === "bulleted" || format === "quote"
      ? toggleLines(text.value, a, b, format)
      : toggleInline(text.value, a, b, format),
  );
  nextTick(updateFormatBar);
}
useEscapeLayer(
  () => !!formatBar.value,
  () => {
    const el = input.value;
    if (el) el.setSelectionRange(el.selectionEnd, el.selectionEnd);
    formatBar.value = null;
  },
);
async function send() {
  const value = text.value.trim();
  if (!value) return;
  formatBar.value = null;
  if (props.send && !props.edit) {
    const draft = text.value;
    text.value = "";
    try {
      const withPreview = linkCard.value?.state === "ready";
      linkCard.value = null;
      closedLinks.clear();
      await props.send({
        text: value,
        reply: props.reply,
        ...(withPreview ? { linkPreview: true } : {}),
      });
    } catch (err) {
      text.value = draft;
      toast(
        err instanceof Error && err.message
          ? err.message
          : t("composer.failed"),
      );
      return;
    }
    emit("clear");
    emit("sent");
    nextTick(() => input.value?.focus());
    return;
  }
  if (props.edit) {
    props.edit.text = value;
    props.edit.edited = true;
    emit("clear");
  } else
    addMessage(props.chatId, {
      text: value,
      ...(props.reply
        ? {
            reply: quoteText(props.reply),
            replyAuthor: quoteAuthor.value,
            replyOut: props.reply.out,
            replyId: props.reply.id,
            ...(quoteThumb.value ? { replyThumb: quoteThumb.value } : {}),
          }
        : {}),
    });
  text.value = "";
  emit("clear");
  emit("sent");
  nextTick(() => input.value?.focus());
}
function key(e: KeyboardEvent) {
  if (e.isComposing) return;
  if (snippetKey(e)) return;
  if ((e.metaKey || e.ctrlKey) && !e.altKey) {
    const k = e.key.toLowerCase();
    const format: Format | null =
      k === "b" && !e.shiftKey
        ? "bold"
        : k === "i" && !e.shiftKey
          ? "italic"
          : k === "x" && e.shiftKey
            ? "strike"
            : null;
    if (format) {
      e.preventDefault();
      applyFormat(format);
      return;
    }
  }
  if (e.key === "Enter" && (e.shiftKey || !state.enterSend) && input.value) {
    const edit = continueList(
      text.value,
      input.value.selectionStart,
      input.value.selectionEnd,
    );
    if (edit) {
      e.preventDefault();
      applyEdit(edit);
      return;
    }
  }
  if (e.key === "Enter" && !e.shiftKey && state.enterSend) {
    e.preventDefault();
    send();
  }
}
function attach(e: MouseEvent) {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const height = api.value ? (props.contacts ? 300 : 256) : 360;
  menu.value = { open: !menu.value.open, x: r.left, y: r.top - height };
}
const menuItems = computed(() => {
  const pickItem = (
    label: string,
    icon: string,
    kind: UploadIntent,
    tone: string,
  ) => ({
    label,
    icon,
    tone,
    action: () => pick(kind),
  });
  const items = [
    pickItem(t("composer.attach.document"), "file", "document", "#7f66ff"),
    pickItem(t("composer.attach.media"), "image", "media", "#007bfc"),
    {
      label: t("composer.attach.camera"),
      icon: "camera",
      tone: "#ff2e74",
      action: () => (camera.value = true),
    },
    pickItem(t("composer.attach.audio"), "audio", "audio", "#fa6533"),
  ];
  if (api.value) {
    if (props.contacts)
      items.push({
        label: t("composer.attach.contact"),
        icon: "user",
        tone: "#009de2",
        action: () => emit("contacts"),
      });
    items.push(
      pickItem(t("composer.attach.sticker"), "sticker", "sticker", "#02a698"),
    );
    return items;
  }
  return [
    ...items,
    {
      label: t("composer.attach.contact"),
      icon: "user",
      tone: "#009de2",
      action: () => (modal.value = "contact"),
    },
    {
      label: t("composer.attach.poll"),
      icon: "poll",
      tone: "#ffbc38",
      action: () => (modal.value = "poll"),
    },
    {
      label: t("composer.attach.event"),
      icon: "event",
      tone: "#ff2e74",
      action: () => (modal.value = "event"),
    },
    pickItem(t("composer.attach.sticker"), "sticker", "sticker", "#02a698"),
  ];
});
function pick(kind: UploadIntent) {
  intent.value = kind;
  accept.value = acceptFor(kind);
  nextTick(() => fileInput.value?.click());
}
function fileChange(e: Event) {
  const selected = Array.from((e.target as HTMLInputElement).files || []);
  (e.target as HTMLInputElement).value = "";
  if (selected.length) openFiles(selected, intent.value);
}
/** Opens the media composer with files (attach menu, paste, drop, camera). */
function openFiles(files: File[], kind: UploadIntent = "media") {
  if (!files.length || !props.attachments) return;
  expressions.value = false;
  menu.value.open = false;
  media.value = { files, intent: kind };
}
function onPaste(e: ClipboardEvent) {
  const files = Array.from(e.clipboardData?.files ?? []);
  if (!files.length) return;
  e.preventDefault();
  openFiles(files, "media");
}
async function sendPrepared(items: PreparedFile[]) {
  media.value = null;
  if (props.sendFiles) {
    emit("sent");
    try {
      await props.sendFiles(items);
    } catch (err) {
      toast(
        err instanceof Error && err.message
          ? err.message
          : t("composer.failed"),
      );
    }
    emit("sent");
    nextTick(() => input.value?.focus());
    return;
  }
  for (const item of items) {
    const kind: Message["kind"] =
      item.kind === "document" ? "file" : (item.kind ?? "file");
    addMessage(props.chatId, {
      text: item.caption || (kind === "file" ? item.original.name : ""),
      kind,
      src: item.previewUrl ?? URL.createObjectURL(item.file ?? item.original),
      name: item.file?.name ?? item.original.name,
      size: ((item.file ?? item.original).size / 1024).toFixed(1) + " KB",
      duration: "0:04",
    });
  }
  emit("sent");
}
function onCapture(file: File) {
  camera.value = false;
  openFiles([file], "media");
}
function choose(value: string, kind: "text" | "sticker" | "image") {
  if (kind === "text") {
    text.value += value;
    input.value?.focus();
  } else {
    addMessage(props.chatId, { text: value, kind });
    emit("sent");
  }
}
function sendPoll() {
  const clean = options.value.map((s) => s.trim()).filter(Boolean);
  if (!question.value.trim() || clean.length < 2) return;
  addMessage(props.chatId, {
    text: question.value,
    kind: "poll",
    options: clean,
    votes: clean.map(() => 0),
    selected: [],
    multiple: multiple.value,
    anonymous: anonymous.value,
    expiresAt: endPoll.value && endDate.value ? endDate.value : undefined,
  });
  modal.value = "";
  question.value = "";
  options.value = ["", ""];
  emit("sent");
}
function stopTracks() {
  stream?.getTracks().forEach((t) => t.stop());
  stream = undefined;
  clearInterval(timer);
  clearInterval(levelTimer);
  void audioCtx?.close().catch(() => undefined);
  audioCtx = undefined;
  dropPreview();
}
/** Reads the microphone's loudness for the live waveform; without it the bar just stays flat. */
function listen(source: MediaStream) {
  levels.value = [];
  try {
    audioCtx = new AudioContext();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    audioCtx.createMediaStreamSource(source).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    levelTimer = setInterval(() => {
      if (paused.value) return;
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += v * v;
      levels.value.push(Math.min(1, Math.sqrt(sum / buf.length) * 6));
    }, 100);
  } catch {
    // The waveform is a nicety; recording goes on without it.
  }
}
function dropPreview() {
  previewAudio?.pause();
  previewAudio = undefined;
  if (preview.value) URL.revokeObjectURL(preview.value.url);
  preview.value = null;
}
/** What was recorded until the pause, ready to be heard. */
function makePreview() {
  dropPreview();
  const url = URL.createObjectURL(
    new Blob(chunks, { type: recorder?.mimeType || "audio/webm" }),
  );
  const audio = new Audio(url);
  preview.value = { url, playing: false, position: 0 };
  audio.ontimeupdate = () => {
    if (preview.value) preview.value.position = audio.currentTime;
  };
  audio.onplay = () => preview.value && (preview.value.playing = true);
  audio.onpause = () => preview.value && (preview.value.playing = false);
  audio.onended = () => {
    if (!preview.value) return;
    preview.value.playing = false;
    preview.value.position = 0;
  };
  previewAudio = audio;
}
function togglePreview() {
  const audio = previewAudio;
  if (!audio) return;
  if (audio.paused) void audio.play().catch(() => undefined);
  else audio.pause();
}
/** Opus in WebM when the browser can (the API turns it into an OGG voice note). */
function recorderType(): string | undefined {
  const preferred = [
    "audio/webm;codecs=opus",
    "audio/ogg;codecs=opus",
    "audio/webm",
  ];
  return preferred.find((type) => MediaRecorder.isTypeSupported?.(type));
}
function finishBlob() {
  recordedBlob = new Blob(chunks, { type: recorder?.mimeType || "audio/webm" });
  recorded.value = URL.createObjectURL(recordedBlob);
}
async function startRecording() {
  recordingError.value = "";
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    chunks = [];
    seconds.value = 0;
    recorded.value = "";
    recordedBlob = null;
    const type = recorderType();
    recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    recorder.ondataavailable = (e) => {
      chunks.push(e.data);
      if (paused.value && recorder?.state === "paused") makePreview();
    };
    listen(stream);
    recorder.onstop = () => {
      finishBlob();
      stopTracks();
    };
    recorder.start();
    recording.value = true;
    paused.value = false;
    timer = setInterval(() => {
      if (!paused.value) seconds.value++;
    }, 1000);
  } catch {
    recordingError.value = t("composer.mic_denied");
    toast(t("composer.mic_unavailable"));
  }
}
/** WhatsApp's red pause: stops for a listen; the microphone goes on recording. */
function pauseRecording() {
  if (!recorder) return;
  if (recorder.state === "recording") {
    paused.value = true;
    recorder.pause();
    // The data so far arrives right after; that is when the preview is made.
    recorder.requestData();
  } else if (recorder.state === "paused") {
    dropPreview();
    recorder.resume();
    paused.value = false;
  }
}
function cancelRecording() {
  if (recorder && recorder.state !== "inactive") {
    recorder.onstop = () => stopTracks();
    recorder.stop();
  }
  stopTracks();
  recording.value = false;
  paused.value = false;
  if (recorded.value) URL.revokeObjectURL(recorded.value);
  recorded.value = "";
  recordedBlob = null;
}
async function stopRecorder() {
  if (!recorder || recorder.state === "inactive") return;
  await new Promise<void>((resolve) => {
    recorder!.onstop = () => {
      finishBlob();
      stopTracks();
      resolve();
    };
    recorder!.stop();
  });
}
async function sendRecording() {
  await stopRecorder();
  if (!recorded.value || !recordedBlob) return;
  if (props.sendVoice) {
    sendingVoice.value = true;
    try {
      await props.sendVoice({
        blob: recordedBlob,
        durationMs: seconds.value * 1000,
      });
    } catch (err) {
      toast(
        err instanceof Error && err.message
          ? err.message
          : t("composer.failed"),
      );
      sendingVoice.value = false;
      return;
    }
    sendingVoice.value = false;
    URL.revokeObjectURL(recorded.value);
    recorded.value = "";
    recordedBlob = null;
    recording.value = false;
    emit("sent");
    keepFocus();
    return;
  }
  addMessage(props.chatId, {
    text: t("composer.voice_note"),
    kind: "audio",
    src: recorded.value,
    duration: duration.value,
  });
  recorded.value = "";
  recording.value = false;
  emit("sent");
}
onBeforeUnmount(cancelRecording);
defineExpose({ openFiles });
</script>
<template>
  <div class="composer-area">
    <Transition name="pop"
      ><EmojiPicker
        v-if="expressions"
        @choose="choose"
        @close="expressions = false"
    /></Transition>
    <div v-if="recordingError" class="recording-error">
      {{ recordingError
      }}<button @click="recordingError = ''">{{ t("common.close") }}</button>
    </div>
    <!-- As on WhatsApp Web: the controls on the right; paused, the recording can be heard. -->
    <div v-if="recording" class="recording-bar">
      <IconButton
        icon="trash"
        :label="t('composer.voice_discard')"
        @click="cancelRecording"
      />
      <template v-if="!paused">
        <span class="recording-dot" /><span class="recording-time">{{
          duration
        }}</span>
        <div class="recording-wave" aria-hidden="true">
          <i
            v-for="(h, i) in liveBars"
            :key="i"
            :class="{ dot: h === null, empty: h === undefined }"
            :style="typeof h === 'number' ? { height: h + 'px' } : undefined"
          />
        </div>
        <IconButton
          class="recording-pause"
          icon="pause"
          :label="t('composer.voice_pause')"
          @click="pauseRecording"
        />
      </template>
      <template v-else>
        <IconButton
          class="recording-play"
          :icon="preview?.playing ? 'pause' : 'play'"
          :label="
            preview?.playing
              ? t('composer.voice_preview_pause')
              : t('composer.voice_preview')
          "
          :disabled="!preview"
          @click="togglePreview"
        /><span class="recording-time">{{ previewClock }}</span>
        <div class="recording-wave" aria-hidden="true">
          <i
            v-for="(h, i) in pausedBars"
            :key="i"
            :class="{
              dot: h === null,
              played: (i + 0.5) / pausedBars.length <= previewProgress,
            }"
            :style="typeof h === 'number' ? { height: h + 'px' } : undefined"
          />
        </div>
        <IconButton
          class="recording-resume"
          icon="wa-mic"
          :label="t('composer.voice_resume')"
          @click="pauseRecording"
        />
      </template>
      <IconButton
        icon="wa-send"
        :label="t('composer.voice_send')"
        filled
        :disabled="sendingVoice"
        @click="sendRecording"
      />
    </div>
    <div v-if="snippetMatches.length" class="snippet-picker" role="listbox">
      <button
        v-for="(sn, i) in snippetMatches"
        :key="sn.id"
        type="button"
        role="option"
        :aria-selected="i === snippetIndex"
        :class="{ active: i === snippetIndex }"
        @mousedown.prevent="pickSnippet(sn)"
      >
        <strong>{{ sn.title }}</strong>
        <small v-if="sn.shortcut" class="muted">/{{ sn.shortcut }}</small>
        <span>{{ sn.body }}</span>
      </button>
    </div>
    <div
      v-else-if="snippetQuery !== null && snippets?.length"
      class="snippet-picker"
    >
      <p class="muted small">{{ t("composer.no_snippet") }}</p>
    </div>
    <div
      v-if="!recording"
      class="composer-pill"
      :class="{ quoting: !!(reply || edit) }"
    >
      <div
        v-if="reply || edit"
        class="composer-quote"
        :class="{ own: !edit && reply?.out, editing: !!edit }"
      >
        <span class="composer-quote-body">
          <strong>{{ quoteAuthor }}</strong>
          <span class="composer-quote-text">{{ quoteLine }}</span>
        </span>
        <img
          v-if="quoteThumb"
          class="composer-quote-thumb"
          :src="quoteThumb"
          alt=""
        />
        <IconButton
          icon="close"
          :size="22"
          :stroke="2.2"
          :label="t('composer.cancel_reference')"
          @click="
            emit('clear');
            text = '';
          "
        />
      </div>
      <div
        v-if="linkCard"
        class="composer-link"
        :class="{ loading: linkCard.state === 'loading' }"
        :aria-busy="linkCard.state === 'loading'"
      >
        <img
          v-if="linkCard.preview?.imageUrl"
          class="composer-link-image"
          :src="linkCard.preview.imageUrl"
          alt=""
        />
        <span v-else class="composer-link-icon"
          ><Icon name="link" :size="28"
        /></span>
        <span class="composer-link-body">
          <template v-if="linkCard.preview">
            <strong>{{ linkCard.preview.title }}</strong>
            <span
              v-if="linkCard.preview.description"
              class="composer-link-description"
              >{{ linkCard.preview.description }}</span
            >
          </template>
          <span class="composer-link-site">{{
            linkCard.preview?.site ?? siteOf(linkCard.url)
          }}</span>
        </span>
        <IconButton
          icon="close"
          :size="22"
          :stroke="2.2"
          :label="t('composer.link_preview_remove')"
          @click="closeLink"
        />
      </div>
      <div class="composer-row">
        <IconButton
          v-if="attachments"
          :icon="menu.open ? 'close' : 'wa-plus'"
          :label="t('composer.attach.label')"
          :active="menu.open"
          @click="attach"
        /><IconButton
          icon="wa-expressions"
          :label="t('composer.expressions')"
          :active="expressions"
          @click="expressions = !expressions"
        /><textarea
          ref="input"
          v-model="text"
          rows="1"
          :placeholder="t('composer.placeholder')"
          :aria-label="t('composer.placeholder')"
          :spellcheck="state.spellcheck"
          @keydown="key"
          @paste="onPaste"
          @input="fitInput"
          @select="updateFormatBar"
          @mouseup="updateFormatBar"
          @keyup="updateFormatBar"
          @scroll="formatBar && updateFormatBar()"
          @blur="formatBar = null"
        /><FormatBar
          v-if="formatBar"
          :x="formatBar.x"
          :y="formatBar.y"
          :active="formatBar.active"
          @apply="applyFormat"
        /><IconButton
          v-if="text.trim()"
          :icon="edit ? 'check' : 'wa-send'"
          :label="edit ? t('composer.save_edit') : t('composer.send')"
          :filled="true"
          @click="send"
        /><IconButton
          v-else-if="audio"
          icon="wa-mic"
          :label="t('composer.voice')"
          @click="startRecording"
        /><IconButton
          v-else
          icon="wa-send"
          :label="t('composer.send')"
          disabled
        />
      </div>
    </div>
    <input
      ref="fileInput"
      hidden
      type="file"
      :accept="accept"
      multiple
      @change="fileChange"
    />
    <Dropdown
      :open="menu.open"
      :x="menu.x"
      :y="menu.y"
      @close="menu.open = false"
      :items="menuItems"
    />
    <Teleport
      v-if="media"
      :to="overlayTarget || 'body'"
      :disabled="!overlayTarget"
    >
      <MediaComposer
        :files="media.files"
        :intent="media.intent"
        @close="media = null"
        @send="sendPrepared"
      />
    </Teleport>
    <CameraCapture v-if="camera" @close="camera = false" @capture="onCapture" />
    <Modal
      :open="!!modal"
      :title="
        modal === 'poll'
          ? t('composer.poll.title')
          : modal === 'event'
            ? t('composer.event.title')
            : t('composer.contact.title')
      "
      @close="modal = ''"
      ><div v-if="modal === 'poll'" class="poll-form stack">
        <label class="wa-field"
          ><span>{{ t("composer.poll.question") }}</span
          ><input
            v-model="question"
            :placeholder="t('composer.poll.question_placeholder')"
            :aria-label="t('composer.poll.question')"
            maxlength="255" /></label
        ><strong>{{ t("composer.poll.options") }}</strong>
        <div v-for="(_, i) in options" :key="i" class="row">
          <label class="wa-field grow"
            ><input
              v-model="options[i]"
              :aria-label="t('composer.poll.option', { n: i + 1 })"
              :placeholder="t('composer.poll.option_placeholder')"
              maxlength="100" /></label
          ><IconButton
            icon="grip"
            :label="t('composer.poll.move_up', { n: i + 1 })"
            @click="
              i > 0 &&
              ([options[i - 1], options[i]] = [options[i], options[i - 1]])
            "
          /><IconButton
            v-if="options.length > 2"
            icon="close"
            :label="t('composer.poll.remove', { n: i + 1 })"
            @click="options.splice(i, 1)"
          />
        </div>
        <Button
          v-if="options.length < 12"
          variant="ghost"
          icon="plus"
          @click="options.push('')"
          >{{ t("composer.poll.add") }}</Button
        ><Switch
          v-model="multiple"
          :label="t('composer.poll.multiple')"
        /><Switch
          v-model="anonymous"
          :label="t('composer.poll.anonymous')"
        /><Switch v-model="endPoll" :label="t('composer.poll.end')" /><label
          v-if="endPoll"
          class="wa-field"
          ><span>{{ t("composer.poll.ends_at") }}</span
          ><input type="datetime-local"
        /></label>
      </div>
      <div v-else-if="modal === 'event'" class="stack">
        <label class="wa-field"
          ><span>{{ t("composer.event.name") }}</span
          ><input
            v-model="eventName"
            :placeholder="t('composer.event.name_placeholder')" /></label
        ><label class="wa-field"
          ><span>{{ t("composer.event.when") }}</span
          ><input type="datetime-local" v-model="eventDate" /></label
        ><label class="wa-field"
          ><span>{{ t("composer.event.description") }}</span
          ><textarea
            :placeholder="t('composer.event.description_placeholder')"
          /></label
        ><Switch :model-value="true" :label="t('composer.event.call_link')" />
      </div>
      <div v-else-if="modal === 'contact'" class="stack">
        <p class="muted">{{ t("composer.contact.demo_hint") }}</p>
        <Select
          v-model="contact"
          :label="t('composer.contact.pick')"
          :options="state.chats.filter((c) => !c.group).map((c) => c.name)"
        />
      </div>
      <template #footer
        ><Button variant="secondary" @click="modal = ''">{{
          t("common.cancel")
        }}</Button
        ><Button
          v-if="modal === 'poll'"
          :disabled="
            !question.trim() || options.filter((o) => o.trim()).length < 2
          "
          icon="send"
          @click="sendPoll"
          >{{ t("composer.send") }}</Button
        ><Button
          v-else-if="modal === 'event'"
          :disabled="!eventName.trim() || !eventDate"
          icon="send"
          @click="
            addMessage(chatId, {
              text: eventName,
              kind: 'event',
              name: new Date(eventDate).toLocaleString(locale),
            });
            modal = '';
            emit('sent');
          "
          >{{ t("composer.event.title") }}</Button
        ><Button
          v-else
          icon="send"
          @click="
            addMessage(chatId, {
              text: t('composer.contact.message', { name: contact }),
              kind: 'contact',
              name: contact,
            });
            modal = '';
            emit('sent');
          "
          >{{ t("composer.contact.share") }}</Button
        ></template
      ></Modal
    >
  </div>
</template>
